import React from "react";
import MenuItem from "../menu-item";
import Globalize from "globalize";
import { Runway } from "components/icons/runway";
import { Booth } from "components/icons/booth";
import { useFetchSitesMenu, IMenuSite, MenuSitesKeys } from "containers/sites-manager/hooks";
import classNames from "classnames";
import { DATATEST } from "constants/integration";

interface ISiteProps {
    viewport: any;
    label: string;
    Icon: any;
    menuSitesKey: MenuSitesKeys;
    menuSites?: IMenuSite[];
    onClick: (event) => void;
    menuSitesLoading: boolean;
    nullStateLabel: string;
    dataTest: string;
}

interface ISitesProps {
    viewport: any;
    isOpen: boolean;
}

const Site: React.FunctionComponent<ISiteProps> = ({
    label,
    viewport,
    Icon,
    menuSites,
    menuSitesKey,
    onClick,
    menuSitesLoading,
    nullStateLabel,
    dataTest,
}) => {
    const submenuItemsSections = React.useMemo(() => {
        const submenuItems = menuSites?.find((menuSite) => menuSite.key === menuSitesKey);
        return submenuItems?.sections;
    }, [menuSites, menuSitesKey]);
    return (
        <MenuItem
            Icon={Icon}
            label={label}
            className="navBarLink"
            viewport={viewport}
            onClick={onClick}
            dataTest={dataTest}
        >
            {!menuSitesLoading &&
                submenuItemsSections?.map((subMenuEntry, index) => (
                    <MenuItem
                        key={index}
                        label={subMenuEntry.name}
                        className={classNames({ isSectionChild: !!subMenuEntry.depth })}
                        onClick={subMenuEntry.slug ? undefined : onClick}
                        to={`/site/${subMenuEntry.slug}`}
                        viewport={viewport}
                    />
                ))}
            {!menuSitesLoading && !submenuItemsSections?.length && (
                <MenuItem label={nullStateLabel} onClick={onClick} viewport={viewport} />
            )}
        </MenuItem>
    );
};

const Sites: React.FunctionComponent<ISitesProps> = ({ viewport, isOpen }) => {
    const [enabled, setEnabled] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (isOpen && !enabled) {
            setEnabled(true);
        }
    }, [isOpen, enabled]);

    const { data: menuSites, isLoading: menuSitesLoading } = useFetchSitesMenu({ enabled });

    const handleClick = React.useCallback((event) => event.preventDefault(), []);

    return (
        <React.Fragment>
            <Site
                Icon={Booth}
                label={Globalize.formatMessage("Digital Trade Shows")}
                viewport={viewport}
                menuSitesKey={MenuSitesKeys.DigitalTradeShow}
                onClick={handleClick}
                menuSites={menuSites}
                menuSitesLoading={menuSitesLoading}
                nullStateLabel={Globalize.formatMessage("There are currently no trade shows")}
                dataTest={DATATEST.NAV.DIGITAL_TRADE_SHOWS}
            />
            <Site
                Icon={Runway}
                label={Globalize.formatMessage("Digital Fashion Weeks")}
                viewport={viewport}
                menuSitesKey={MenuSitesKeys.DigitalFashionShow}
                onClick={handleClick}
                menuSites={menuSites}
                menuSitesLoading={menuSitesLoading}
                nullStateLabel={Globalize.formatMessage("There are currently no fashion weeks")}
                dataTest={DATATEST.NAV.DIGITAL_FASHION_WEEKS}
            />
        </React.Fragment>
    );
};

export default Sites;
